import {
  ActionGroup,
  Alert,
  Button,
  Card,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  TextInput,
} from '@patternfly/react-core';
import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../../api';
import type { ApiResponseClientCreate } from '../../types/api/ApiResponseClient';
import type { ClientCreateRequest } from '../../types/models';
import type { JoinOfferData } from './JoinOffer';

export interface CreateOrganizationFormProps {
  refreshUser: () => Promise<void>;
  onBlocked: (offer: JoinOfferData) => void;
}

// POST /clients validation errors arrive as a { field: message } map under `data` on a
// status:'error' response. The wrapper types `data` as the success-only union (ClientCreateData),
// so we read the known field keys loosely — through `unknown`, never `any` — mirroring how
// RecordSetFormModal surfaces per-field errors.
const FIELD_KEYS = ['name', 'accounting_email', 'vat_number'] as const;

function readFieldErrors(data: unknown): Record<string, string> {
  const result: Record<string, string> = {};
  if (data && typeof data === 'object') {
    const map = data as Record<string, unknown>;
    for (const key of FIELD_KEYS) {
      const value = map[key];
      if (typeof value === 'string') {
        result[key] = value;
      }
    }
  }
  return result;
}

/**
 * Create-organization form for a clientless user. POSTs /clients and handles both success
 * outcomes: a created client (re-fetch the user → the gate drops into the app) or a blocked
 * join offer (handed up to the container, which flips to the offer step). All feedback is an
 * inline Alert / per-field helper text — the notification drawer is not mounted on a gate screen.
 */
export function CreateOrganizationForm({ refreshUser, onBlocked }: CreateOrganizationFormProps) {
  const apiFetch = useApiFetch();
  const auth = useAuth();
  // The email the user signed up with (OIDC claim) — shown as guidance under the accounting email.
  const signupEmail = auth.user?.profile?.email;

  const [name, setName] = useState('');
  const [accountingEmail, setAccountingEmail] = useState('');
  const [vatNumber, setVatNumber] = useState('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setFormError(null);
    setFieldErrors({});

    // Include vat_number only when non-empty — an empty/omitted VAT never collides (§3.1).
    const requestBody: ClientCreateRequest = {
      name: name.trim(),
      accounting_email: accountingEmail.trim(),
      ...(vatNumber.trim() ? { vat_number: vatNumber.trim() } : {}),
    };

    try {
      const response = await apiFetch('/clients', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const body = (await response.json()) as ApiResponseClientCreate;

      // Success is status:'success' + the data discriminator — never response.ok / 422 / 'fail'.
      if (body.status === 'success' && body.data) {
        if ('blocked' in body.data && body.data.blocked === true) {
          // Blocked → hand the join offer up; the container flips to the 'offer' step.
          onBlocked({
            reason: body.data.reason,
            organization: body.data.organization,
            canJoin: body.data.can_join,
            submittedVat: requestBody.vat_number ?? '',
          });
          return;
        }
        if ('client' in body.data) {
          // Created → re-fetch the user; the gate re-evaluates and drops into the app.
          await refreshUser();
          return; // this component unmounts once onboarding_state becomes 'active'
        }
      }

      // Error path: per-field map under body.data, form-level message under body.message.
      const nextFieldErrors = readFieldErrors(body.data);
      if (Object.keys(nextFieldErrors).length > 0) {
        setFieldErrors(nextFieldErrors);
      } else {
        setFormError(body.message ?? 'We could not create your organization. Please try again.');
      }
    } catch {
      setFormError('We could not reach the server. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card style={{ maxWidth: '600px' }}>
      <CardTitle>Set up your organization</CardTitle>
      <CardBody>
        {formError && (
          <Alert variant="danger" isInline title="Could not create organization" className="pf-v6-u-mb-md">
            {formError}
          </Alert>
        )}
        <Form onSubmit={(event) => void handleSubmit(event)}>
          <FormGroup label="Organization name" isRequired fieldId="create-org-name">
            <TextInput
              id="create-org-name"
              type="text"
              value={name}
              validated={fieldErrors.name ? 'error' : 'default'}
              onChange={(_event, value) => {
                setName(value);
                clearFieldError('name');
              }}
            />
            {fieldErrors.name && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{fieldErrors.name}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>

          <FormGroup label="Accounting email" isRequired fieldId="create-org-accounting-email">
            <TextInput
              id="create-org-accounting-email"
              type="email"
              value={accountingEmail}
              validated={fieldErrors.accounting_email ? 'error' : 'default'}
              onChange={(_event, value) => {
                setAccountingEmail(value);
                clearFieldError('accounting_email');
              }}
            />
            {fieldErrors.accounting_email ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{fieldErrors.accounting_email}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>
                    {signupEmail && (
                      <>
                        You signed up with <strong>{signupEmail}</strong>.{' '}
                      </>
                    )}
                    This accounting email is where invoices and accounting information will be sent.
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>

          <FormGroup label="VAT number" fieldId="create-org-vat-number">
            <TextInput
              id="create-org-vat-number"
              type="text"
              value={vatNumber}
              validated={fieldErrors.vat_number ? 'error' : 'default'}
              onChange={(_event, value) => {
                setVatNumber(value);
                clearFieldError('vat_number');
              }}
            />
            {fieldErrors.vat_number ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{fieldErrors.vat_number}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>Optional. Leave blank if your organization has no VAT number.</HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>

          <ActionGroup>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
              spinnerAriaValueText="Creating organization"
            >
              Create organization
            </Button>
          </ActionGroup>
        </Form>
      </CardBody>
    </Card>
  );
}
